import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";

/**
 * MarketingPlansSection — always-visible sponsorship/marketing ladder on
 * the owner dashboard.
 *
 * Why always-visible: we want owners to discover sponsorship plans the
 * moment they sign in. Even owners with zero claims see the section
 * (buttons disabled with copy "Claim a listing first") so they know the
 * monetisation path exists.
 *
 * Per-card behaviour:
 *   - 0 claims  -> button disabled, copy "Claim a listing first"
 *   - 1 claim   -> button auto-applies to that claim
 *   - 2+ claims -> dropdown lets the owner pick which listing to boost
 */
export default function MarketingPlansSection({ tiers, claims, token }) {
  if (!tiers || tiers.length === 0) return null;

  // Sort tiers by amount asc for a clean ladder
  const ladder = [...tiers].sort((a, b) => a.amount - b.amount);

  return (
    <section
      data-testid="owner-plans-section"
      className="mt-8 rounded-3xl border border-[#E5E1D8] bg-gradient-to-br from-white to-[#FFF3EE]/40 p-6"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E1A624] to-[#D95D39] flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-medium text-[#2A3B2C]">
            Get more diners with a Featured listing
          </h2>
          <p className="text-sm text-[#647466] mt-1 leading-relaxed">
            Featured listings sit at the top of map search results, appear on
            the home page carousel, and unlock a gold-trim card design. Owners
            who sponsor for 6 months see{" "}
            <span className="font-semibold text-[#2A3B2C]">
              2.4× more profile views
            </span>{" "}
            on average. One-time payment per term — no auto-renew.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ladder.map((t) => (
          <PlanCard key={t.id} tier={t} claims={claims} token={token} />
        ))}
      </div>

      {claims.length === 0 ? (
        <p className="mt-4 text-xs text-[#647466] italic">
          Claim a restaurant first, then return here to boost it.
        </p>
      ) : (
        <p className="mt-4 text-xs text-[#647466]">
          Plans apply to your listings above. Need help picking? Email{" "}
          <a
            href="mailto:hello@alkoncierge.com"
            className="text-[#D95D39] hover:underline"
          >
            hello@alkoncierge.com
          </a>
          .
        </p>
      )}
    </section>
  );
}

function PlanCard({ tier, claims, token }) {
  const [selectedClaim, setSelectedClaim] = useState(
    claims.length === 1 ? claims[0].claim_id : ""
  );
  const [busy, setBusy] = useState(false);

  const handleBoost = async () => {
    if (!selectedClaim) {
      toast.error("Pick which listing to boost");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(
        "/owner/extend",
        {
          claim_id: selectedClaim,
          tier_id: tier.id,
          origin_url: window.location.origin,
        },
        { headers: { "X-Owner-Token": token } }
      );
      try {
        sessionStorage.setItem(
          "halal_sponsor_session",
          JSON.stringify({
            session_id: res.data.session_id,
            tier_id: tier.id,
          })
        );
      } catch {
        /* sessionStorage may be unavailable */
      }
      track(EVENTS.SPONSOR_CHECKOUT_STARTED, {
        claim_id: selectedClaim,
        tier_id: tier.id,
        source: "owner_dashboard",
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error("Couldn't start checkout", {
        description: err?.response?.data?.detail || err.message,
      });
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`owner-plan-${tier.id}`}
      className={`relative rounded-2xl border p-4 bg-white flex flex-col gap-3 ${
        tier.popular ? "border-[#D95D39] shadow-sm" : "border-[#E5E1D8]"
      }`}
    >
      {tier.popular && (
        <span className="absolute -top-2.5 right-4 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#D95D39] text-white">
          Most Popular
        </span>
      )}
      {tier.discount_percent > 0 && !tier.popular && (
        <span className="absolute -top-2.5 right-4 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#2A3B2C] text-white">
          SAVE {tier.discount_percent}%
        </span>
      )}
      <div>
        <h3 className="font-display text-lg font-medium text-[#2A3B2C]">
          {tier.name}
        </h3>
        <p className="text-xs text-[#647466] mt-0.5">{tier.tagline}</p>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-display text-3xl font-semibold text-[#2A3B2C]">
          ${Math.round(tier.amount).toLocaleString()}
        </span>
        {tier.original_amount && tier.original_amount > tier.amount && (
          <span className="text-xs text-[#8A9A8B] line-through">
            ${Math.round(tier.original_amount).toLocaleString()}
          </span>
        )}
        <span className="text-[10px] uppercase tracking-wider text-[#8A9A8B] ml-1">
          / {tier.duration_days}d
        </span>
      </div>
      <ul className="space-y-1 text-xs text-[#647466] flex-1">
        {(tier.perks || []).slice(0, 3).map((p) => (
          <li key={p} className="flex items-start gap-1.5">
            <Sparkles className="w-3 h-3 mt-0.5 text-[#D95D39] shrink-0" />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      {claims.length > 1 && (
        <select
          data-testid={`owner-plan-${tier.id}-listing`}
          value={selectedClaim}
          onChange={(e) => setSelectedClaim(e.target.value)}
          className="h-9 w-full rounded-lg border border-[#E5E1D8] bg-white px-2 text-xs"
        >
          <option value="">— pick a listing —</option>
          {claims.map((c) => (
            <option key={c.claim_id} value={c.claim_id}>
              {c.restaurant_name}
            </option>
          ))}
        </select>
      )}
      <button
        type="button"
        data-testid={`owner-plan-${tier.id}-buy`}
        onClick={handleBoost}
        disabled={busy || claims.length === 0 || !selectedClaim}
        className="h-10 w-full rounded-full bg-[#D95D39] hover:bg-[#C54E2C] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center justify-center gap-2"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : claims.length === 0 ? (
          "Claim a listing first"
        ) : (
          <>
            <Sparkles className="w-3.5 h-3.5" />
            Boost for {tier.duration_days} days
          </>
        )}
      </button>
    </div>
  );
}
